<script setup lang="ts">
/*
  "You have N pending invitations", on the two home pages — the Designer's project list and the
  Operator's dataspace list — leading to the invitations section of the account.

  Dismissible for the session only: the dismissal is a number held in memory (the count when it
  was dismissed), so a reload brings the banner back, nothing is written to the device, and it
  comes back on its own when another invitation arrives. It is not drawn for an account whose
  email the provider has not verified: there is nothing to say a count about.

  The link leaves the page. Both homes are outside the Operator's `?nonav` pages (they are
  `/designer` and `/`), so nothing here leads out of a view that may not.
*/
import { mdiEmailFastOutline } from "@mdi/js";
import { HabBanner, HabButton } from "@livereader/habitus-vue";
import { useRouter } from "vue-router";
import { useInvitations } from "@/shared/composables/useInvitations";

const router = useRouter();
const { count, bannerVisible, dismissBanner } = useInvitations();

/*
  A real anchor, so a modified click opens a tab the browser's own way and a plain one routes
  instead of reloading — the same shape as the Operator's "Open Designer" button.
*/
const href = "/account?section=invitations";
function follow(event: MouseEvent) {
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
	event.preventDefault();
	void router.push({ path: "/account", query: { section: "invitations" } });
}
</script>

<template>
	<HabBanner
		v-if="bannerVisible"
		tone="info"
		:icon="mdiEmailFastOutline"
		:title="$t('invitations.banner.title', { n: count }, count)"
		:dismiss-label="$t('common.actions.close')"
		@dismiss="dismissBanner"
	>
		<template #action>
			<HabButton
				as="a"
				:href="href"
				variant="text"
				size="sm"
				@click="follow"
			>
				{{ $t("invitations.banner.action") }}
			</HabButton>
		</template>
	</HabBanner>
</template>
